// MOYRI · ACTIVE SWAP — specialist. MoyRi's object is the SWAP: a malady holding an
// ORDER seat, replaced by that seat's deity. Reads the ACTIVE SWAPS the way
// olmps/SpectrumScreen.tsx does: the seat holder is the SIGN of the §5 seat score
// (useSeats: 4 x (D - M) / marks due over 30 days; seats start at 0 and read "—"
// until 8 days of marks). A seat still at "—" whose health-check intake (context only,
// never the seat) flagged a malady is also an active SWAP so it can be worked from day one.
// Malady names come ONLY from SWAP_DATA (via getSwapEntry). No "pick malady -> pick deity".
//
// Not read here: this week's reps. SpectrumScreen derives them by running the per-tool
// DERIVERS (several table scans per seat), which is too heavy for a widget adapter.
import { supabase } from '../../../../lib/supabase';
import { useCompassConfig } from '../../../../olmps/sectorConfig';
import { loadState } from '../../../../olmps/spectrum-data';
import { MOYRI_SEATS, spectrumWord, type MoyriSeat } from '../../../../olmps/moyriSeats';
import { formatSeat } from '../../../../olmps/seatScore';
import { useSeats } from '../../../../olmps/useSeats';
import { getSwapEntry } from '../../../../olmps/swap-data';
import { GEAR_LABEL } from '../../../roster';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, useLoad } from './util';

const BASE = { id: 'moyri', toolId: 'moyri', orderFunction: 'regulate', title: 'ACTIVE SWAP', deityName: 'MOYRI', agentId: 'moyri' } as const;

interface ActiveSwap {
  seat: MoyriSeat;
  maladyName: string;
  /** §5 seat score; null = "—" (under 8 days of marks). */
  position: number | null;
}

/** A checked seat: its SWAP_DATA malady name, and whether the intake flagged a malady (start_position < 0) — context only. */
interface CheckedSeat {
  seat: MoyriSeat;
  maladyName: string;
  flagged: boolean;
}

const proper = (s: string) => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s);

async function loadCheckedSeats(userId: string): Promise<CheckedSeat[]> {
  const { data: intakes } = await supabase
    .from('dmt_intake')
    .select('tool_id, start_position, taken_at')
    .eq('user_id', userId)
    .in('tool_id', MOYRI_SEATS.map((s) => s.toolId))
    .order('taken_at', { ascending: false });
  const latest = new Map<string, number | null>();
  for (const r of (intakes ?? []) as { tool_id: string; start_position: number | null }[]) {
    if (!latest.has(r.tool_id)) latest.set(r.tool_id, r.start_position);
  }

  const out: CheckedSeat[] = [];
  await Promise.all(
    MOYRI_SEATS.filter((s) => latest.has(s.toolId)).map(async (seat) => {
      const { maladySubtype } = await loadState(supabase, userId, seat.toolId);
      const maladyId = maladySubtype && seat.maladies.includes(maladySubtype) ? maladySubtype : seat.maladies[0];
      const maladyName = getSwapEntry(seat.toolId, maladyId)?.maladyName;
      if (!maladyName) return; // no SWAP_DATA entry → never invent a name
      out.push({ seat, maladyName, flagged: (latest.get(seat.toolId) ?? 1) < 0 });
    }),
  );
  return out;
}

export function useMoyriWidget(c: WidgetCtx): OrbWidgetModel {
  const { config } = useCompassConfig(c.userId);
  const { seats: board } = useSeats(c.userId);
  const { value: intake } = useLoad<CheckedSeat[]>(c.userId ? () => loadCheckedSeats(c.userId as string) : null, [c.userId, c.tick]);

  if (!intake) return emptyModel(BASE, c, 'No active SWAP');

  const swaps: ActiveSwap[] = [];
  for (const f of intake) {
    const sc = board[f.seat.toolId];
    // The seat score decides; "—" falls back to the intake flag.
    if (sc.holder === 'malady') swaps.push({ seat: f.seat, maladyName: f.maladyName, position: sc.score });
    else if (sc.score === null && f.flagged) swaps.push({ seat: f.seat, maladyName: f.maladyName, position: null });
  }
  if (!swaps.length) return emptyModel(BASE, c, 'No active SWAP');

  // Worst seat score first (most negative); seats still at "—" last; ties keep ORDER gear order.
  const order = (s: ActiveSwap) => MOYRI_SEATS.findIndex((x) => x.gear === s.seat.gear);
  const sorted = [...swaps].sort((a, b) => (a.position ?? 1) - (b.position ?? 1) || order(a) - order(b));
  const top = sorted[0];
  const deity = proper(top.seat.name);
  const monitoring = config.swapEnabled[top.seat.gear] !== false;
  const holds = top.position != null && top.position < 0;
  const seatStr = top.position == null ? '—' : formatSeat(board[top.seat.toolId]);

  const lines = [
    `${top.maladyName} → ${deity} (${GEAR_LABEL[top.seat.gear].name} seat)`,
    `Seat ${seatStr} · ${spectrumWord(top.position)}`,
    ...(monitoring ? [] : ['Monitoring is off for this seat']),
    ...(sorted.length > 1 ? [`${sorted.length - 1} other active SWAP${sorted.length > 2 ? 's' : ''}`] : []),
  ];

  return {
    ...BASE,
    status: holds ? 'attention' : 'active',
    summary: `${top.maladyName} → ${deity} · ${seatStr}`,
    metrics: [
      { label: 'Seat', value: GEAR_LABEL[top.seat.gear].name },
      { label: 'Position', value: seatStr, tone: holds ? 'warning' : 'neutral' },
      { label: 'State', value: spectrumWord(top.position) },
    ],
    alert: holds ? `${top.maladyName} holds the ${GEAR_LABEL[top.seat.gear].name} seat` : null,
    lines,
    agentId: 'moyri',
    primaryAction: { label: 'Run replacement', run: () => c.openTool('moyri'), tone: holds ? 'gold' : 'ghost' },
    signals: { active: true, alerted: holds, related: false },
  };
}
